import { useState } from 'react';
import { Check, Copy, KeyRound, Loader2, Mail } from 'lucide-react';

interface CopyResetLinkModalProps {
  open: boolean;
  /** Name of the account owner, shown in the dialog copy. */
  userName?: string;
  /** Email the reset link would normally be sent to. */
  userEmail?: string;
  /**
   * Generates the reset link. Resolves with the URL on success, or `null` on
   * failure (the caller surfaces the error) so the dialog stays open.
   */
  onConfirm: () => Promise<string | null>;
  onClose: () => void;
}

/**
 * In-app dialog for the "copy reset link" flow. It first asks for confirmation,
 * then shows the generated link with a copy button. Replaces the browser's
 * native confirm/alert popups.
 */
export function CopyResetLinkModal({
  open,
  userName,
  userEmail,
  onConfirm,
  onClose,
}: CopyResetLinkModalProps) {
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const copyToClipboard = async (value: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      return false;
    }
  };

  const handleClose = () => {
    setLink(null);
    setCopied(false);
    onClose();
  };

  const handleConfirm = async () => {
    setLoading(true);
    const url = await onConfirm();
    if (url) {
      setLink(url);
      setCopied(await copyToClipboard(url));
    }
    setLoading(false);
  };

  const handleCopyAgain = async () => {
    if (!link) return;
    setCopied(await copyToClipboard(link));
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!loading) handleClose();
      }}
    >
      <div
        className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {link ? (
          <>
            <div className="p-6 space-y-3">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg shrink-0 bg-success/10">
                  <Check size={20} className="text-success" />
                </div>
                <div className="space-y-1 min-w-0">
                  <h3 className="text-lg font-semibold text-text">Reset link generated</h3>
                  <p className="text-sm text-text-muted">
                    The link for{' '}
                    <span className="text-text font-medium">{userName || 'this user'}</span>{' '}
                    {copied
                      ? 'has been copied to your clipboard.'
                      : 'is ready below — copy it and share it securely.'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-lg bg-background border border-border px-3 py-2">
                <input
                  readOnly
                  value={link}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 bg-transparent text-xs text-text outline-none"
                />
                <button
                  type="button"
                  onClick={handleCopyAgain}
                  className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              <p className="text-xs text-warning">
                This link expires in 30 minutes, works only once, and replaces any previously issued reset link.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 space-y-3">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg shrink-0 bg-primary/10">
                  <KeyRound size={20} className="text-primary" />
                </div>
                <div className="space-y-1 min-w-0">
                  <h3 className="text-lg font-semibold text-text">Copy reset link?</h3>
                  <p className="text-sm text-text-muted">
                    A new password reset link will be generated for{' '}
                    <span className="text-text font-medium">{userName || 'this user'}</span> and
                    copied to your clipboard. No email will be sent.
                  </p>
                </div>
              </div>

              {userEmail && (
                <div className="flex items-center gap-2 rounded-lg bg-background border border-border px-3 py-2">
                  <Mail size={14} className="text-text-muted shrink-0" />
                  <span className="text-sm text-text truncate">{userEmail}</span>
                </div>
              )}

              <p className="text-xs text-warning">
                Any previously issued reset link for this user will stop working.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}
                Generate &amp; Copy
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
